import {type ButtonProps, Typography, useTransition} from '@logistics-frontend/blocks'
import {useI18n} from '@logistics-frontend/core/i18n'
import {type PropsWithChildren, useState} from 'react'

import type {Props} from './PaymentCardList.types'
import type {PaymentCard} from './types'
import {buildKey} from './utils'

type State = {
  card?: PaymentCard
  agreement?: string
  checked: boolean
}
type AgreementProps = PropsWithChildren<{
  error?: string
  checked: boolean
  onChange: VoidFunction
}>

const MAX_CARDS_AMOUNT = 5

export function usePaymentCardList(props: Props) {
  const t = useI18n()
  const {
    cards,
    card: cardProp,
    agreement: agreementProp,
    fetchBoundCard,
    onSuccess,
    title = t('card_list_modal.select_payment_method'),
    subtitle = t('card_list_modal.bound_message'),
  } = props
  const [{card, agreement, checked}, setState] = useState<State>({
    card: cardProp,
    agreement: agreementProp,
    checked: true,
  })
  const [{isPending, error}, startTransition] = useTransition<PaymentCard>(undefined, {key: buildKey('bound')})

  const translates = {
    title,
    subtitle,
    button: isPending
      ? t('phoenix.add_new_card.pending', 'Привязываем карту...')
      : error
      ? t('phoenix.add_new_card.error', 'Не удалось привязать карту, повторить?')
      : t('phoenix.add_new_card.submit'),
  } as const

  function changeHandler(nextCard: PaymentCard) {
    setState(prevState => ({
      ...prevState,
      card: nextCard.id === prevState.card?.id ? undefined : nextCard,
    }))
  }

  const buttonProps: ButtonProps = {
    disabled: !(card && checked),
    variant: isPending ? 'default' : error ? 'error' : 'action',
    loading: isPending,
    onPress:
      card && checked
        ? () => {
            startTransition(() => fetchBoundCard(card), {
              onCommit: onSuccess,
            })
          }
        : undefined,
  }
  const agreementProps: AgreementProps | undefined = agreement
    ? {
        error: !checked ? t('profile.signup.validation.checkbox') : undefined,
        checked: checked,
        children: <Typography dangerouslySetInnerHTML={{__html: agreement}} variant="body2" />,
        onChange() {
          setState(prevState => ({
            ...prevState,
            checked: !prevState.checked,
          }))
        },
      }
    : undefined

  return {
    addDisabled: cards.length >= MAX_CARDS_AMOUNT,
    cards,
    currentCard: card,
    changeHandler,
    translates,
    buttonProps,
    agreementProps,
  } as const
}
